import { App, Spin } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import { MobileTaskCard } from '@/components/mobile/MobileTaskCard';
import { TASK_STATUS } from '@/constants/task';
import { ROUTES } from '@/constants/routes';
import { ApiError } from '@/services/api/client';
import * as tasksApi from '@/services/api/tasks';
import type { QueryTasksParams, TaskListItem } from '@/types/task';

type TaskTab = 'all' | 'in_progress' | 'completed' | 'overdue';

const TAB_ITEMS: { key: TaskTab; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'in_progress', label: '进行中' },
  { key: 'completed', label: '已完成' },
  { key: 'overdue', label: '已逾期' },
];

function parseTaskTab(value: string | null): TaskTab {
  if (value === 'in_progress' || value === 'completed' || value === 'overdue') {
    return value;
  }
  return 'all';
}

function queryForTab(tab: TaskTab): Pick<QueryTasksParams, 'status' | 'overdueOnly'> {
  switch (tab) {
    case 'in_progress':
      return { status: TASK_STATUS.IN_PROGRESS };
    case 'completed':
      return { status: TASK_STATUS.COMPLETED };
    case 'overdue':
      return { overdueOnly: true };
    default:
      return {};
  }
}

export function EmployeeTasksPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = parseTaskTab(searchParams.get('tab'));
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<TaskListItem[]>([]);
  const [total, setTotal] = useState(0);

  const switchTab = (tab: TaskTab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (tab === 'all') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tab);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const queryParams = useMemo(() => queryForTab(activeTab), [activeTab]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const result = await tasksApi.fetchTasks({
        page: 1,
        pageSize: 100,
        ...queryParams,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载任务列表失败';
      message.error(text);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [message, queryParams]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  return (
    <div className="mobile-workbench">
      <div className="mobile-segment-tabs mobile-segment-tabs--compact">
        {TAB_ITEMS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`mobile-segment-tabs__item${activeTab === tab.key ? ' mobile-segment-tabs__item--active' : ''}`}
            onClick={() => switchTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <section className="mobile-section">
        <div className="mobile-section__header">
          <span className="mobile-section__title">我的任务</span>
          <span className="mobile-section__meta">共 {total} 项</span>
        </div>

        {loading ? (
          <div className="mobile-card" style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : items.length === 0 ? (
          <MobileEmpty message="暂无任务" />
        ) : (
          items.map((task) => (
            <MobileTaskCard
              key={task.id}
              task={task}
              onClick={() => navigate(ROUTES.mobile.employee.taskDetail(task.id))}
            />
          ))
        )}
      </section>
    </div>
  );
}
